import { useNavigate } from "react-router-dom";
import { ADMIN_COLORS as C } from "./adminColors";
import { useTimeline } from "hooks/ovizia/useTimeline";
import { TIMELINE_EVENT_TYPES } from "hooks/ovizia/timelineApi";
import Icon from "components/common/Icon";

const RANGES = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "all", label: "All time" },
];

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "6px 8px",
        borderRadius: 6,
        border: `1px solid ${C.border}`,
        background: "#fff",
        color: C.text,
        cursor: "pointer",
      }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function EntityChip({ label, value, onOpen }) {
  if (!value) return null;
  return (
    <button
      onClick={onOpen}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: 11,
        fontFamily: "monospace",
        fontWeight: 600,
        color: C.textMuted,
        background: C.bgSurface,
        border: `1px solid ${C.border}`,
        borderRadius: 6,
        padding: "3px 8px",
        cursor: "pointer",
      }}
      title={`Open ${label.toLowerCase()}`}
    >
      {label}: {value}
    </button>
  );
}

function TimelineItem({ event, isLast, onOpenEntity }) {
  const isError = event.category === "error";
  const fg = isError ? C.red : C.green;
  const bg = isError ? C.redBg : C.greenBg;

  return (
    <div style={{ display: "flex", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={event.icon} size={14} style={{ color: fg }} />
        </div>
        {!isLast && <div style={{ flex: 1, width: 1, background: C.border, marginTop: 2 }} />}
      </div>

      <div style={{ paddingBottom: 22, flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: fg, textTransform: "uppercase", letterSpacing: "0.03em" }}>
            {event.label}
          </span>
          <span style={{ fontSize: 11, color: C.textFaint }}>
            {new Date(event.createdAt).toLocaleString()}
          </span>
        </div>
        <div style={{ fontSize: 13, color: C.text, marginTop: 3 }}>{event.message}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          <EntityChip label="Website" value={event.websiteId} onOpen={() => onOpenEntity(`/ovizia/websites?websiteId=${event.websiteId}`)} />
          <EntityChip label="Workspace" value={event.workspaceId} onOpen={() => onOpenEntity("")} />
          <EntityChip label="User" value={event.userId} onOpen={() => onOpenEntity(`/olitask/users/${event.userId}`)} />
          <EntityChip label="Generation" value={event.generationId} onOpen={() => onOpenEntity("/ovizia/generation-monitoring")} />
          <EntityChip label="Error" value={event.errorId} onOpen={() => onOpenEntity("/ovizia/activity-timeline")} />
        </div>
      </div>
    </div>
  );
}

export default function ActivityTimelinePage() {
  const navigate = useNavigate();
  const {
    events,
    loading,
    query,
    setQuery,
    category,
    setCategory,
    type,
    setType,
    range,
    setRange,
    refresh,
  } = useTimeline();

  const typeOptions = TIMELINE_EVENT_TYPES.filter(
    (t) => !category || t.category === category,
  );

  const handleCategoryChange = (next) => {
    setCategory(next);
    // reset type filter if it no longer belongs to the selected category
    if (type && next && TIMELINE_EVENT_TYPES.find((t) => t.value === type)?.category !== next) {
      setType("");
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "24px 28px",
        fontFamily: "Inter, system-ui, sans-serif",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 20,
          flexShrink: 0,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.green, margin: 0 }}>
            Activity & error timeline
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            Every website, generation, and publishing event across the system — in one feed.
          </p>
        </div>
        <button
          onClick={refresh}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            color: C.textMuted,
            border: `1px solid ${C.border}`,
            borderRadius: 7,
            background: "#fff",
            padding: "7px 12px",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Icon name="refresh" size={13} /> Refresh
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap", flexShrink: 0 }}>
        <div
          style={{
            flex: 1,
            minWidth: 220,
            display: "flex",
            alignItems: "center",
            gap: 8,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: "9px 12px",
            background: "#fff",
          }}
        >
          <Icon name="search" size={15} style={{ color: C.textFaint }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by message, website/workspace/user/generation ID..."
            style={{ border: "none", outline: "none", flex: 1, fontSize: 13, fontFamily: "inherit" }}
          />
        </div>
        <Select
          value={type}
          onChange={setType}
          options={[{ value: "", label: "All event types" }, ...typeOptions.map((t) => ({ value: t.value, label: t.label }))]}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { value: "", label: "All" },
            { value: "activity", label: "Activity" },
            { value: "error", label: "Errors" },
          ].map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleCategoryChange(opt.value)}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${category === opt.value ? C.green : C.border}`,
                background: category === opt.value ? C.greenBg : "#fff",
                color: category === opt.value ? C.green : C.textMuted,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 12px",
                borderRadius: 999,
                border: `1px solid ${range === r.value ? C.text : C.border}`,
                background: range === r.value ? C.text : "#fff",
                color: range === r.value ? "#fff" : C.textMuted,
                cursor: "pointer",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          background: "#fff",
          padding: "20px 20px 0",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {loading ? (
          <div style={{ padding: "20px 0 40px", textAlign: "center", color: C.textFaint, fontSize: 13 }}>
            Loading timeline…
          </div>
        ) : events.length === 0 ? (
          <div style={{ padding: "20px 0 40px", textAlign: "center", color: C.textFaint, fontSize: 13 }}>
            No events match these filters.
          </div>
        ) : (
          events.map((event, i) => (
            <TimelineItem
              key={event.id}
              event={event}
              isLast={i === events.length - 1}
              onOpenEntity={(path) => navigate(path)}
            />
          ))
        )}
      </div>
    </div>
  );
}